const ERROR_TARGET_CLASS = 't-input';
const ERROR_WRAPPER_CLASS = 't-input-wrap';
const ERROR_MESSAGE_CLASS = 't-error-msg';
const ERROR_STATE_CLASS = 'is-error';
const SHAKE_STATE_CLASS = 'is-shaking';
const SOUND_DEDUPE_MS = 120;
const SHAKE_CLEANUP_MS = 320;

const feedbackAssociations = new WeakMap();
const shakeCleanupTimers = new WeakMap();
let lastSoundAt = 0;

const isElement = (value) => value instanceof HTMLElement;

const resolveTarget = (target) => {
  if (!isElement(target)) {
    return null;
  }

  if (target.matches('[data-error-shake-target], .t-input')) {
    return target;
  }

  return (
    target.closest('[data-error-shake-target]') ||
    target.closest('.t-input') ||
    target.closest('.input-clear') ||
    target
  );
};

const resolveWrapper = (target, wrapper, message) => {
  if (isElement(wrapper)) {
    return wrapper;
  }

  return (
    target?.closest('.t-input-wrap') ||
    message?.closest('.t-input-wrap') ||
    target?.closest('form') ||
    message?.parentElement ||
    null
  );
};

const playErrorSound = () => {
  const now = performance.now();

  if (now - lastSoundAt < SOUND_DEDUPE_MS) {
    return;
  }

  lastSoundAt = now;
  window.SigmaThemeAudio?.playError();
};

const clearShakeTimer = (target) => {
  const timer = shakeCleanupTimers.get(target);

  if (timer !== undefined) {
    window.clearTimeout(timer);
    shakeCleanupTimers.delete(target);
  }
};

const finishShake = (target) => {
  clearShakeTimer(target);
  target.classList.remove(SHAKE_STATE_CLASS);
};

const restartShake = (target) => {
  if (!isElement(target)) {
    return;
  }

  clearShakeTimer(target);
  target.classList.add(ERROR_TARGET_CLASS, ERROR_STATE_CLASS);
  target.classList.remove(SHAKE_STATE_CLASS);
  void target.offsetWidth;
  target.classList.add(SHAKE_STATE_CLASS);

  const cleanup = () => finishShake(target);
  target.addEventListener('animationend', cleanup, { once: true });
  shakeCleanupTimers.set(target, window.setTimeout(cleanup, SHAKE_CLEANUP_MS));
};

const show = ({ target = null, wrapper = null, message = null, sound = true } = {}) => {
  const resolvedTarget = resolveTarget(target);
  const resolvedMessage = isElement(message) ? message : null;
  const resolvedWrapper = resolveWrapper(resolvedTarget, wrapper, resolvedMessage);

  resolvedWrapper?.classList.add(ERROR_WRAPPER_CLASS, ERROR_STATE_CLASS);

  if (resolvedMessage) {
    resolvedMessage.classList.add(ERROR_MESSAGE_CLASS);
    resolvedMessage.hidden = false;
    feedbackAssociations.set(resolvedMessage, { target: resolvedTarget, wrapper: resolvedWrapper });
  }

  restartShake(resolvedTarget);

  if (sound) {
    playErrorSound();
  }
};

const clear = ({ target = null, wrapper = null, message = null } = {}) => {
  const resolvedMessage = isElement(message) ? message : null;
  const association = resolvedMessage ? feedbackAssociations.get(resolvedMessage) : null;
  const resolvedTarget = resolveTarget(target) || association?.target || null;
  const resolvedWrapper = resolveWrapper(resolvedTarget, wrapper || association?.wrapper, resolvedMessage);

  if (resolvedTarget) {
    clearShakeTimer(resolvedTarget);
    resolvedTarget.classList.remove(ERROR_STATE_CLASS, SHAKE_STATE_CLASS);
  }

  if (resolvedWrapper && !resolvedWrapper.querySelector('.t-input.is-error')) {
    resolvedWrapper.classList.remove(ERROR_STATE_CLASS);
  }

  if (resolvedMessage) {
    feedbackAssociations.delete(resolvedMessage);
  }
};

window.SigmaErrorFeedback = Object.freeze({ clear, show });

document.addEventListener(
  'invalid',
  (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement)) {
      return;
    }

    event.target.setAttribute('aria-invalid', 'true');
    show({
      target: event.target,
      wrapper: event.target.closest('.t-input-wrap') || event.target.closest('form'),
    });
  },
  true,
);

document.addEventListener('input', (event) => {
  if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement)) {
    return;
  }

  if (!event.target.validity.valid) {
    return;
  }

  if (event.target.hasAttribute('aria-invalid')) {
    event.target.setAttribute('aria-invalid', 'false');
  }

  clear({
    target: event.target,
    wrapper: event.target.closest('.t-input-wrap') || event.target.closest('form'),
  });
});

window.addEventListener('DOMContentLoaded', () => {
  const initialTargets = Array.from(document.querySelectorAll('.t-input.is-error'));

  initialTargets.forEach(restartShake);

  if (initialTargets.length > 0) {
    playErrorSound();
  }
});
